<script setup lang="ts">
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue';
import { CheckIcon, SelectorIcon } from '@heroicons/vue/solid';
import type { PropType } from 'vue';
import { useGobyStore } from '~/stores/goby';
import type { ProfileWithCoin } from '~/types/store';
import { avatarGradientStyles } from '~/utilities/avatar-gradient';

const props = defineProps({ modelValue: { type: Object as PropType<ProfileWithCoin | undefined>, required: true } });
const emit = defineEmits(['update:modelValue']);

const { modelValue: selectedProfile } = toRefs(props);
const gobyStore = useGobyStore();

if (gobyStore.profiles !== undefined) {
  emit('update:modelValue', gobyStore.profiles[0]);
} else {
  const unsubscribe = gobyStore.$subscribe((mutation, state) => {
    if (state.profiles !== undefined) {
      emit('update:modelValue', state.profiles?.[0]);
      unsubscribe();
    }
  });
}
</script>
<template>
  <Listbox
    as="div"
    :model-value="modelValue"
    :disabled="gobyStore.profiles?.length === 0"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="relative mt-2">
      <ListboxButton
        class="sm:text-md relative w-full min-w-[20rem] cursor-default rounded-md border border-neutral-300 bg-white py-4 pl-4 pr-12 text-left shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-black dark:text-white"
      >
        <span v-if="selectedProfile" class="flex items-center">
          <img
            v-if="selectedProfile.avatar_uri"
            alt="selected profile avatar"
            class="h-12 w-12 rounded-full"
            :src="selectedProfile.avatar_uri"
          />
          <span
            v-else
            class="h-12 w-12 rounded-full bg-gradient-to-br"
            :style="{
              ...avatarGradientStyles(selectedProfile.id),
              flexShrink: 0,
            }"
          ></span>
          <div class="ml-3 flex flex-col">
            <span class="block truncate"> {{ selectedProfile.name }}</span>
            <span class="text-xs text-neutral-500 dark:text-neutral-400">{{
              selectedProfile.encoded_id.substring(0, 36) + '...'
            }}</span>
          </div>
        </span>
        <span v-else-if="gobyStore.profiles === undefined" class="flex items-center">
          <span class="block truncate">Loading...</span>
        </span>
        <span v-else class="flex items-center">
          <span class="block truncate">No profile available</span>
        </span>
        <span class="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
          <SelectorIcon class="h-6 w-6 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
        </span>
      </ListboxButton>

      <transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ListboxOptions
          class="sm:text-md absolute z-10 mt-2 max-h-72 w-full overflow-auto rounded-md bg-white p-2 text-base shadow-lg ring-1 ring-neutral-300 focus:outline-none dark:bg-black dark:ring-neutral-600"
        >
          <ListboxOption
            v-for="profile in gobyStore.profiles"
            :key="profile.id"
            v-slot="{ active, selected }"
            as="template"
            :value="profile"
          >
            <li
              :class="[
                active ? 'bg-neutral-100 dark:bg-neutral-800 dark:text-white' : 'text-neutral-900 dark:text-neutral-50',
                'relative cursor-default select-none rounded-md py-3 pl-4 pr-4',
              ]"
            >
              <div class="flex items-center">
                <img v-if="profile.avatar_uri" class="h-8 w-8 rounded-full" :src="profile.avatar_uri" />
                <span
                  v-else
                  class="h-8 w-8 rounded-full bg-gradient-to-br"
                  :style="{
                    ...avatarGradientStyles(profile.id),
                    flexShrink: 0,
                  }"
                ></span>

                <div class="ml-3 flex flex-col">
                  <span :class="[selected ? 'font-semibold' : 'font-normal']" class="block truncate">
                    {{ profile.name }} {{ profile.didCoin.locked ? '(locked)' : '' }}</span
                  >
                  <span class="text-xs text-neutral-500 dark:text-neutral-400">{{
                    profile.encoded_id.substring(0, 36) + '...'
                  }}</span>
                </div>
              </div>

              <span
                v-if="selected"
                :class="[
                  active ? 'text-emerald-500' : 'text-emerald-600',
                  'absolute inset-y-0 right-0 flex items-center pr-2',
                ]"
              >
                <CheckIcon class="h-5 w-5" aria-hidden="true" />
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
</template>
